import {IconAsset} from '@sail/icons/types';
import {css, Icon, Link, view} from '@sail/ui/deprecated';
import {
  ConversationArticle,
  ArticlesMessage as ArticlesMessageType,
  Conversation,
} from '@stripe-internal/conversations-client';
import {useCallback} from 'react';
import {defineMessages, FormattedMessage, MessageDescriptor} from 'react-intl';
import {useAnalytics} from '../../../AnalyticsProvider';
import {useTheme} from '../../../ThemeProvider';
import {Card} from '../../../Chat/Card';
import {useNewUniversalHelpDesign} from '../../../FeatureFlagsProvider';
import {ArticleNext} from './ArticleNext';

const messages: Record<string, Record<string, MessageDescriptor>> = {
  default: defineMessages({
    articleHeaderTitle: {
      id: 'conversations_ui.conversation.log.messages.articles.headerTitle',
      description:
        'The support site title shown when displaying support articles.',
      defaultMessage: 'Stripe Support',
    },
    docsArticleHeaderTitle: {
      id: 'conversations_ui.conversation.log.messages.articles.DocsHeaderTitle',
      description:
        'The support site title shown when displaying Stripe Docs articles.',
      defaultMessage: 'Stripe Docs',
    },
  }),
  link: defineMessages({
    articleHeaderTitle: {
      id: 'conversations_ui.conversation.log.messages.articles.LinkHeaderTitle.headerTitle',
      description:
        'The link site title shown when displaying Link support articles.',
      defaultMessage: '{linkBrandName} Support',
    },
    docsArticleHeaderTitle: {
      id: 'conversations_ui.conversation.log.messages.articles.LinkHeaderTitle.DocsHeaderTitle',
      description:
        'The link site title shown when displaying Stripe Docs articles.',
      defaultMessage: 'Stripe Docs',
    },
  }),
};

type StripeIconProps = {
  icon: IconAsset;
};

const StripeIcon = ({icon}: StripeIconProps) => (
  <Icon
    icon={icon}
    css={{
      width: 400,
      height: 400,
      borderRadius: 'xsmall',
    }}
    data-testid="stripe-icon"
  />
);

type ArticleProps = {
  conversation: Conversation;
  article: ConversationArticle;
};

const Article = ({conversation, article}: ArticleProps) => {
  const analytics = useAnalytics();
  const theme = useTheme();

  const onPress = useCallback(() => {
    analytics.action('presented_article_clicked', {
      articleUrl: article.url,
    });
    conversation.action({name: 'article-visited', data: article});
  }, [analytics, article, conversation]);

  const messageKey = theme.messageKey || 'default';
  const messagesForTheme = messages[messageKey];
  const title =
    article.collection === 'stripe_docs'
      ? messagesForTheme.docsArticleHeaderTitle
      : messagesForTheme.articleHeaderTitle;

  return (
    /**
     * Using article.url as target instead of '_blank' to prevent opening multiple
     * tabs of the same article from the support widget. If the article has
     * already been opened from here, the already-opened tab will be focused.
     */
    <Link
      data-testid="article-link"
      href={article.url}
      target={article.url}
      onPress={onPress}
    >
      <Card hover>
        <view.div
          css={{
            display: css.value('flex'),
            alignItems: css.value('flex-start'),
            gap: 'small',
            width: 'fill',
          }}
        >
          <view.div>
            <StripeIcon icon={theme.icon} />
          </view.div>
          <view.div
            css={{
              stack: 'y',
            }}
          >
            <view.span
              css={{font: 'caption', color: 'primary', fontWeight: 'semibold'}}
            >
              <FormattedMessage
                {...title}
                values={{linkBrandName: theme.linkBrandName}}
              />
            </view.span>
            <view.span
              css={{font: 'caption', color: css.value(theme.linkColor)}}
            >
              {article.title}
            </view.span>
          </view.div>
        </view.div>
      </Card>
    </Link>
  );
};

type Props = {
  conversation: Conversation;
  message: ArticlesMessageType;
};

export const ArticlesMessage = ({conversation, message}: Props) => {
  const useNewDesign = useNewUniversalHelpDesign();

  const articles = message.articles.map((article) =>
    useNewDesign ? (
      <ArticleNext
        key={article.id}
        conversation={conversation}
        article={article}
      />
    ) : (
      <Article key={article.id} conversation={conversation} article={article} />
    ),
  );

  return (
    <view.div
      data-testid="articles-message"
      css={useNewDesign ? {width: 'fill', backgroundColor: 'surface'} : {}}
    >
      {articles}
    </view.div>
  );
};
